import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NbButtonModule, NbSpinnerModule } from '@nebular/theme';
import { filter, map, take, withLatestFrom } from 'rxjs';
import { InvoiceSettingInterface, PdfCreatorService } from 'src/app/core/services/pdf-creator.service';
import { createInvoiceSettings } from 'src/app/booking/booking-voucher/invoice';
import { CarCalculationsService } from 'src/app/core/services/car-calculations.service';
import { ReservationInterface } from 'src/app/core/interfaces/reservations.interfaces';
import { EquipmentInterface } from 'src/app/core/interfaces/rates.interfaces';
import { ToastService } from 'src/app/core/services/toast.service';
import { FleetsDataService } from '../../core/services/fleets-data.service';
import { ReservationDataService } from '../../core/services/reservation-data.service';
import { CrxTrackingService } from '../../core/services/crx-tracking.service';
import { getFleetLogoUrlByIdAndRateGroup } from '../../core/helpers';
import { RentalPeriodService } from '../../core/services/rental-period.service';
import { reservationTotal } from './reservation-total';
import { FleetInterface } from '../../core/interfaces/fleet.interface';

@Component({
  selector: 'car-booking-voucher',
  templateUrl: './booking-voucher.component.html',
  styleUrls: ['./booking-voucher.component.scss'],
  providers: [CarCalculationsService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NbButtonModule,
    NbSpinnerModule,
  ],
})
export class BookingVoucherComponent implements OnDestroy {
  @ViewChild('voucher') voucher!: ElementRef<HTMLElement>;

  reservation_hash = '';
  order_id = 0;

  reservation$ = this.reservationsDataService.reservations$.pipe(filter(Boolean));
  reservationLoading$ = this.reservationsDataService.isLoading$
  fleetLogoUrl$ = this.fleetsDataService.fleets$.pipe(
    filter((res: FleetInterface[]) => Boolean(res && res.length)),
    withLatestFrom(this.reservation$),
    map(([fleets, reservation]) => getFleetLogoUrlByIdAndRateGroup(reservation.vehicle.fleet_id, (reservation.rental_rate.vehicle_charges.find((e) => e.ota_code === 1)?.description ?? ''), fleets)),
  );

  equipmentTotal$ = this.reservation$.pipe(
    map((reservation: ReservationInterface) =>
      reservation.equipment.reduce(
        (prev: number, curr: EquipmentInterface) => prev + (+curr.charge.amount + +(curr.charge.tax_amount?.total ?? 0)) * curr.quantity,
        0) * reservation.rental_days
    )
  );

  reservationDate = new Date();

  constructor(
    private route: ActivatedRoute,
    private toastService: ToastService,
    private fleetsDataService: FleetsDataService,
    private reservationsDataService: ReservationDataService,
    private crxTrackingService: CrxTrackingService,
    public carCalculationsService: CarCalculationsService,
    private pdfCreatorService: PdfCreatorService,
    private rentalPeriodService: RentalPeriodService,
  ) {
    this.fleetsDataService.loadFleets();

    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      this.order_id = params['o'];
      this.reservation_hash = params['h'];

      this.reservationsDataService.validatePayment(this.order_id, this.reservation_hash);
    });

    this.reservation$
      .pipe(take(1))
      .subscribe((reservation: ReservationInterface) => {
        this.carCalculationsService.selectedCar = reservation;
        // Fire CRX tracking pixel after reservation is loaded
        this.fireCrxTrackingPixel(reservation);
      });
  }

  savePdf(print = false): void {
    this.pdfCreatorService.saveVoucher(this.voucher.nativeElement, print);
  }

  getEquipmentString(equipment: EquipmentInterface[]): string {
    return equipment.map(item => `${item.quantity} ${item.description}`).join(', ');
  }

  downloadInvoice(): void {
    this.reservation$
      .pipe(
        withLatestFrom(this.equipmentTotal$),
        map(([reservation, equipmentTotal]) => createInvoiceSettings(reservation, this.carCalculationsService.getTotalAmountWithVat(equipmentTotal, reservation.rental_days))),
        take(1))
      .subscribe((invoiceData: InvoiceSettingInterface) => this.pdfCreatorService.downloadInvoice(invoiceData));
  }

  fireCrxTrackingPixel(reservation: ReservationInterface): void {
    const totalAmount = reservationTotal(reservation, this.rentalPeriodService);

    // Format dates to ISO 8601 format (YYYY-MM-DDTHH:MM)
    const pickupTime = this.formatDateToISO8601(reservation.veh_rental_core.pickup_datetime);
    const dropoffTime = this.formatDateToISO8601(reservation.veh_rental_core.return_datetime);

    // Fire the tracking pixel
    this.crxTrackingService.fireConfirmationPixel({
      crx_company_id: null, // Will be populated from cookie
      crx_redirect_id: null, // Will be populated from cookie
      reservation_number: reservation.reservation_number,
      total_charge: totalAmount.toFixed(2),
      currency: reservation.currency_code || 'EUR',
      pickup_time: pickupTime,
      dropoff_time: dropoffTime,
      pickup_loc_id: reservation.veh_rental_core.pickup_desk_code,
      dropoff_loc_id: reservation.veh_rental_core.return_desk_code,
    });
  }

  formatDateToISO8601(dateString: string): string {
    // Convert date to ISO 8601 format: YYYY-MM-DDTHH:MM
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  ngOnDestroy() {
    this.reservationsDataService.clearReservation();
  }

}
